import * as ToastPrimitive from "@radix-ui/react-toast";
import { useCallback, useMemo, useRef, useState } from "react";
import styles from "./ToastProvider.module.scss";
import uuidv4 from "../util/uuidv4";
import Toast, { ToastProps } from "./Toast";
import { ToastContext } from "./ToastContext";

type InternalToast = ToastProps & {
  id: string;
};

function ToastProvider({ children }: { children: React.ReactNode }) {
  const viewportRef = useRef<HTMLOListElement>(null);
  const [toasts, setToasts] = useState<InternalToast[]>([]);

  const showToast = useCallback((toast: ToastProps) => {
    setToasts((prev) => [...prev, { ...toast, id: uuidv4() }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const onOpenChange = useCallback(
    (open: boolean, id: string) => {
      if (!open) {
        /**
         * Since the focus of the viewport pauses the toast timer, we need to ensure that it is
         * not focused after closing a toast to resume the timer
         */
        if (
          document.activeElement &&
          "blur" in document.activeElement &&
          typeof document.activeElement.blur === "function" &&
          viewportRef.current?.contains(document.activeElement) &&
          document.activeElement instanceof HTMLElement
        ) {
          document.activeElement.blur();
        }

        setTimeout(() => removeToast(id), 400);
      }
    },
    [removeToast],
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right" duration={3000}>
        {children}
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            title={toast.title}
            description={toast.description}
            variant={toast.variant}
            onOpenChange={(open) => onOpenChange(open, toast.id)}
          />
        ))}
        <ToastPrimitive.Viewport
          className={styles.viewport}
          ref={viewportRef}
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export default ToastProvider;
